"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Employer/B2B section (item 10): elevated from main.html's single
 * buried `.card.b2b` into its own bold, dark section with three concrete
 * value props, matching ui-ux-spec.md's required "Employer section"
 * item — domain verification / seat-based access / private-by-default
 * are summarized here; the full admin behavior lives in the product,
 * not marketing copy. The CTA pre-selects the "employer" topic in the
 * contact form below (item 12) instead of opening a separate flow.
 */

import { useState } from "react"
import { Building2, ShieldCheck, Users, Wallet } from "lucide-react"
import { useI18n } from "@/i18n"
import { Reveal } from "./Reveal"
import { EmployerPilotDialog } from "./EmployerPilotDialog"

interface EmployerSectionProps {
  onRequestPilot: () => void
}

export function EmployerSection({ onRequestPilot }: EmployerSectionProps) {
  const { t } = useI18n()
  const [pilotOpen, setPilotOpen] = useState(false)

  const points = [
    {
      icon: Users,
      title: t("landingPage.employer.point1Title"),
      body: t("landingPage.employer.point1Body"),
    },
    {
      icon: Wallet,
      title: t("landingPage.employer.point2Title"),
      body: t("landingPage.employer.point2Body"),
    },
    {
      icon: ShieldCheck,
      title: t("landingPage.employer.point3Title"),
      body: t("landingPage.employer.point3Body"),
    },
  ]

  return (
    <section id="employers" className="scroll-mt-[12vh] border-b border-line bg-green text-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-bb-full bg-paper/10 px-3 py-1 text-xs font-bold tracking-wide uppercase">
              <Building2 aria-hidden="true" className="size-3.5" />
              {t("landingPage.employer.eyebrow")}
            </span>
            <h2 className="mt-3 max-w-2xl text-3xl font-bold md:text-4xl">
              {t("landingPage.employer.heading")}
            </h2>
            <p className="mt-3 max-w-prose text-paper/80">{t("landingPage.employer.lead")}</p>
          </div>
          {/* Owner feedback 2026-08-26 (f): the CTA now visibly opens the
              pilot-request dialog (mailto handoff to hello@bitebalance.de);
              onRequestPilot still pre-fills the contact section topic for
              anyone who scrolls instead. */}
          <button
            type="button"
            onClick={() => {
              onRequestPilot()
              setPilotOpen(true)
            }}
            className="min-h-11 shrink-0 rounded-bb-full bg-lime px-6 text-sm font-bold text-ink transition-transform hover:-translate-y-0.5"
          >
            {t("landingPage.employer.cta")}
          </button>
        </Reveal>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {points.map((point, i) => (
            <Reveal key={point.title} index={i + 1}>
              <article className="flex h-full flex-col gap-3 rounded-bb-xl border border-paper/15 bg-paper/5 p-6">
                <point.icon aria-hidden="true" className="size-6 text-lime" />
                <h3 className="text-lg font-semibold">{point.title}</h3>
                <p className="text-sm text-paper/75">{point.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
      <EmployerPilotDialog open={pilotOpen} onClose={() => setPilotOpen(false)} />
    </section>
  )
}
